import { FC, useState, useCallback } from 'react';
import { useFragment } from 'react-relay';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import graphql from 'babel-plugin-relay/macro';

import DeleteIcon from '@mui/icons-material/Delete';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Checkbox from '@mui/material/Checkbox';
import ConfirmableIconButton from 'components/ConfirmableIconButton';
import MediaThumb from 'components/MediaThumb';

import { useMediaSort } from 'components/MediaEditorSort';
import { MediaEditorThumb_media$key } from '__generated__/MediaEditorThumb_media.graphql';

type Props = {
  media: MediaEditorThumb_media$key;
  selected?: boolean;
  disabled?: boolean;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
};

const MediaEditorThumb: FC<Props> = props => {
  const { selected = false, disabled = false, onDelete, onSelect } = props;
  const [hover, setHover] = useState(false);
  const { sorting } = useMediaSort();

  const media = useFragment(
    graphql`
      fragment MediaEditorThumb_media on Media {
        id

        ...MediaThumb_media
      }
    `,
    props.media
  );

  const { id } = media;

  const handleMouseEnter = useCallback(() => setHover(true), []);

  const handleMouseLeave = useCallback(() => setHover(false), []);

  const handleDeleteClick = useCallback(() => {
    onDelete(id);
  }, [onDelete, id]);

  const handleSelect = useCallback(() => {
    onSelect(id);
  }, [onSelect, id]);

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id, disabled: sorting || disabled });

  return (
    <Grid
      item
      xs={12}
      sm={6}
      md={4}
      lg={3}
      xl={2}
      sx={{
        mb: 2,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...attributes}
      {...listeners}
      ref={setNodeRef}>
      <Box sx={{ position: 'relative' }}>
        {!disabled && (hover || selected) && (
          <Checkbox
            color="primary"
            sx={{ position: 'absolute', top: 0, left: 0 }}
            checked={selected}
            onChange={handleSelect}
          />
        )}

        {!disabled && hover && (
          <Box
            sx={{
              position: 'absolute',
              top: 5,
              right: 0,
            }}>
            <ConfirmableIconButton
              sx={{ cursor: 'pointer' }}
              onConfirm={handleDeleteClick}>
              <DeleteIcon fontSize="small" color="error" />
            </ConfirmableIconButton>
          </Box>
        )}

        <MediaThumb media={media} onClick={handleSelect} />
      </Box>
    </Grid>
  );
};

export default MediaEditorThumb;
